import React from 'react';
import DataTable, {
  SortDirectionEnum,
  TTableColumn,
  TTableSelection
} from '@/components/DataTable';
import { TWalletData } from '@/features/home/types/WalletData';
import { formatAmountOutput } from '@/utils/formatAmount';
import { addFavoriteAccountsModal } from '@/utils/translations';

const ACCOUNT_COLUMN = 'account';

interface AddFavoriteAccountsTableProps {
  wallets: TWalletData[];
  selection: TTableSelection<TWalletData>;
  emptyMessage: string;
  maxBodyHeight?: string;
}

const AddFavoriteAccountsTable: React.FC<AddFavoriteAccountsTableProps> = ({
  wallets,
  selection,
  emptyMessage,
  maxBodyHeight
}) => {
  const columns: TTableColumn<TWalletData>[] = [
    {
      key: ACCOUNT_COLUMN,
      header: addFavoriteAccountsModal.columns.account,
      canSort: true,
      sortAccessor: (wallet) => wallet.name.toLowerCase(),
      cell: (wallet) => (
        <div className="flex flex-col">
          <span className="text-emerge-navy">{wallet.name}</span>
          <span className="text-2xs text-emerge-navy-3">{wallet.accountNumber}</span>
        </div>
      )
    },
    {
      key: 'balance',
      header: addFavoriteAccountsModal.columns.balance,
      cell: (wallet) => `$${formatAmountOutput(String(wallet.balance ?? 0))}`,
      // Pinned so Account (left unset) takes the rest of the width - table-layout: fixed
      // (maxBodyHeight mode) would otherwise split the two evenly, cramping the longer
      // account name/number column to make room a short dollar figure doesn't need.
      width: '110px'
    }
  ];

  return (
    <DataTable<TWalletData>
      columns={columns}
      data={wallets}
      getRowKey={(wallet) => wallet.id}
      itemLabel={addFavoriteAccountsModal.itemLabel}
      emptyMessage={emptyMessage}
      pagination={{ mode: 'none' }}
      sorting={{
        mode: 'client',
        initialSort: { key: ACCOUNT_COLUMN, direction: SortDirectionEnum.ASC }
      }}
      selection={selection}
      maxBodyHeight={maxBodyHeight}
      // The default empty state reserves a tall (20rem+) placeholder area, sized for a normal
      // page-scrolled table. Inside this modal's height-capped table that fights maxBodyHeight
      // instead of cooperating with it - a "no accounts match" row would be forced taller than
      // there's room for and scroll on its own for no reason. This keeps it a compact single
      // line instead, so the table can shrink to fit when there's little/nothing to show.
      emptyStateFixedHeight={false}
    />
  );
};

export default AddFavoriteAccountsTable;
